/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createTenantApiKey } from '../../api';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type ApiKeyDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function ApiKeyDialog({ actionDialogReturn, onNavigate: _onNavigate }: ApiKeyDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'api-key') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit = actionDialog.name.trim().length >= 2;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'api-key' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const apiKeyName = actionDialog.name.trim();
      if (apiKeyName.length < 2) {
        setDialogError('API key name must be at least 2 characters long.');
        return;
      }
      const created = await createTenantApiKey(session.accessToken, {
        name: apiKeyName,
        scopes: actionDialog.scopes
          .split(',')
          .map((scope) => scope.trim())
          .filter(Boolean),
        expiresAt: actionDialog.expiresAt.trim() || undefined
      });
      setWorkspaceVersion((v) => v + 1);
      setActionDialog({
        kind: 'api-key-reveal',
        name: created.name,
        rawKey: created.rawKey
      });
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Cindro API keys</div>
            <h3>Create Cindro API key</h3>
            <p>Give this key a name you&apos;ll recognise in audit logs and the key list.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="CI deploy key"
              minLength={2}
              autoFocus
            />
            <p className="fieldHint">Appears in the key list and audit trail.</p>
          </label>
          <label className="settingsField">
            <span>Scopes</span>
            <input
              value={actionDialog.scopes}
              onChange={(e) => setActionDialog({ ...actionDialog, scopes: e.target.value })}
              placeholder="* or releases:read,releases:write"
            />
            <p className="fieldHint">Comma-separated. Use * to grant all scopes.</p>
          </label>
          <label className="settingsField">
            <span>
              Expires at{' '}
              <em style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</em>
            </span>
            <input
              type="datetime-local"
              value={actionDialog.expiresAt}
              onChange={(e) => setActionDialog({ ...actionDialog, expiresAt: e.target.value })}
            />
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create key'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { ApiKeyDialog };
